import { ImmutableArray, ImmutableObject } from "@hookstate/core"
import { HighlightOff } from "@mui/icons-material"
import EastIcon from "@mui/icons-material/East"
import WestIcon from "@mui/icons-material/West"

import { Divider, Stack } from "@mui/material"
import LinearProgress from "@mui/material/LinearProgress"
import { useRouter } from "next/router"
import { FC, useEffect, useMemo, useState } from "react"

import { Accordion } from "src/components/common/accordion"
import { Button } from "src/components/common/button"
import { Drawer } from "src/components/common/drawer"
import { Icon } from "src/components/common/icon"
import { IconButton } from "src/components/common/iconButton"
import { BasicLayoutStyleProps } from "src/components/layouts/basic"

import { create } from "src/helpers/bem"

import { deepClone } from "src/helpers/deepClone"
import { getPage } from "src/helpers/pages"
import { formatPrice } from "src/helpers/price"
import { Dialogs, mergeDialog, useDialog } from "src/states/dialogs"
import { useLocale } from "src/states/locale"
import {
  CartProduct,
  getCartProductAmount,
  postRefreshCart,
  setCart,
  useCart,
  useCarts,
} from "src/states/shop/carts"
import { getCategories } from "src/states/shop/category"
import { ProductVariant } from "src/states/shop/productVariants"
import { TranslationMessages } from "src/translations"

import { PagesProps } from "src/types/SharedPageProps"

import styles from "./ShopCartDialog.module.scss"
import { ShopCartProductRow } from "./ShopCartProductRow"

const bem = create(styles, "ShopCartDialog")

export type ShopCartDialogProps = {
  messages: TranslationMessages
  pages: PagesProps
  style: BasicLayoutStyleProps
}

export const ShopCartDialog: FC<ShopCartDialogProps> = ({
  messages,
  pages,
  style,
}) => {
  const { loading: loadingCarts } = useCarts()
  const cart = useCart()
  const locale = useLocale()
  const router = useRouter()
  const { open } = useDialog("cartDialog") as Dialogs["cartDialog"]
  const [loading, setLoading] = useState(false)
  const dialogMessage = messages.pages.shop.cart.dialog

  const normalizedProducts = useMemo(() => {
    const cartProducts = deepClone<CartProduct[]>(cart.products)
    const uniqueProducts: CartProduct[] = []
    cartProducts.forEach((product) => {
      const filteredMandatoryProducts: ProductVariant[] = []

      product.productVariant?.mandatoryProducts?.forEach((mandatoryProduct) => {
        if (!mandatoryProduct.isUniqueInOrder) {
          filteredMandatoryProducts.push(mandatoryProduct)
          return
        }

        if (
          uniqueProducts.some((unique) =>
            unique.productVariant?.mandatoryProducts?.some(
              (uniqueProduct) => uniqueProduct.id == mandatoryProduct.id,
            ),
          )
        ) {
          return
        } else {
          filteredMandatoryProducts.push(mandatoryProduct)
        }
      })
      uniqueProducts.push({
        ...product,
        productVariant: {
          ...product.productVariant,
          mandatoryProducts: filteredMandatoryProducts,
        },
      })
    })
    return uniqueProducts as ImmutableObject<CartProduct[]>
  }, [cart.products])

  useEffect(() => {
    setLoading(true)
    const refreshCartAsync = async () =>
      setCart(await postRefreshCart(cart), true)
    refreshCartAsync().finally(() => setLoading(false))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locale])

  const buttonDisabled =
    !normalizedProducts ||
    normalizedProducts?.length === 0 ||
    loadingCarts ||
    loading

  const webshopCartOverviewPage = useMemo(
    () => getPage("Webshop Cart Overview Template", locale, pages),
    [pages, locale],
  )

  const categories = getCategories()

  const renderCategories = () => {
    const categoriesWithProducts = categories.categories.filter((category) =>
      normalizedProducts.some(
        (product) => product.productVariant?.parentCategoryId === category.id,
      ),
    )

    return categoriesWithProducts.map((category, index) => (
      <Accordion
        key={category.id + index.toString()}
        disableGutters
        colorVariant="primary"
        description={getCategorySum(category.id)}
        detailsClassName={bem("accordion")}
        id={category.id}
        label={category.name}
      >
        <Stack
          className={bem("stack")}
          divider={<Divider className={bem("divider")} variant="fullWidth" />}
        >
          {renderProductsByCategory(category.id)}
        </Stack>
      </Accordion>
    ))
  }

  const getCategorySum = (categoryId: string) => {
    const products = normalizedProducts.filter(
      (product) => product.productVariant?.parentCategoryId === categoryId,
    )
    const totalSum = calculateTotalSum(products)
    return totalSum
  }

  const calculateTotalSum = (products: ImmutableArray<CartProduct>) => {
    const productSumList = products.map((product) => {
      const mandatorySum = product.productVariant?.mandatoryProducts?.reduce(
        (sum, mandatory) =>
          sum + (mandatory.basePrice ?? 0) * (mandatory.quantity ?? 1),
        0,
      )

      return (
        (product.productVariant?.basePrice ?? 0) *
          (product.productVariant?.quantity ?? 1) *
          getCartProductAmount(product) +
        (mandatorySum ?? 0)
      )
    })
    const totalSum = productSumList.reduce(
      (acc, currentValue) => acc + currentValue,
      0,
    )
    return formatPrice(totalSum, locale)
  }

  const renderProductsByCategory = (categoryId: string) => {
    return normalizedProducts
      .filter(
        (product) => product.productVariant?.parentCategoryId === categoryId,
      )
      .map((product, index) => (
        <ShopCartProductRow
          key={product.id + index.toString()}
          displayDetails={false}
          item={product}
        />
      ))
  }

  const renderTotalSum = () => {
    return <h1>{calculateTotalSum(normalizedProducts)}</h1>
  }

  return (
    <Drawer
      anchor="right"
      open={open}
      style={style}
      onClose={() => mergeDialog("cartDialog", { open: false })}
      onOpen={() => mergeDialog("cartDialog", { open: true })}
    >
      {(loadingCarts || categories.loading) && (
        <LinearProgress color="primary" />
      )}
      <div className={bem()}>
        <div className={bem("header")}>
          <div className={bem("counter")}>
            <h2 className={bem("counter__title")}>{dialogMessage.title}</h2>
            <Icon className={bem("counter__icon")} name="cart" />
          </div>
          <div className={bem("close")}>
            <IconButton
              color={"primary"}
              icon={HighlightOff}
              onClick={() => mergeDialog("cartDialog", { open: false })}
            />
          </div>
        </div>
        <div className={bem("products")}>{renderCategories()}</div>
        <div className={bem("sum")}>
          <h3>{dialogMessage.total}</h3>
          {renderTotalSum()}
        </div>
        <div className={bem("footer")}>
          <Button
            buttonType="secondary"
            disabled={buttonDisabled}
            startIcon={<WestIcon fontSize="small" />}
            title={dialogMessage.buttons.back}
            variant="outlined"
            onClick={async () => {
              mergeDialog("cartDialog", { open: false })
            }}
          />
          <Button
            buttonType="primary"
            disabled={buttonDisabled}
            endIcon={<EastIcon fontSize="small" />}
            title={dialogMessage.buttons.checkout}
            variant="outlined"
            onClick={async () => {
              mergeDialog("cartDialog", { open: false })
              await router.push({
                pathname: webshopCartOverviewPage?.uri as string,
                query: {
                  cartId: cart.id,
                },
              })
            }}
          />
        </div>
      </div>
    </Drawer>
  )
}
